import { AsyncStateNotification } from '@ui/notifications';
import { createAsyncSelectors } from '@lib/selectors';
import { CurrentPasswordModal } from '@ui/CurrentPasswordModal';
import { Email } from '@ui/form-items';
import { ErrorOrFailStatusCode } from '@sonar-software/sonar-http';
import { FormInstance } from 'antd/es/form';
import { securityActions } from '@sonar-software/sonar-security';

import { Button, Form } from 'antd';
import { COPY_SPECIFIC, NOTIFICATIONS_COPY } from '@sonar-software/sonar-ts-constants';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAction, useCurrentUser, useSonarSelector } from '@hooks';

const patchChangeEmailSelectors = createAsyncSelectors((state) => state.security.authentication.changeEmail.patch);
const buttonText = `${COPY_SPECIFIC.UPDATE} ${COPY_SPECIFIC.EMAIL}`;

export const CurrentUserEmail: React.FC = () => {
	const emailFormRef = useRef<FormInstance>(null);
	const [ showModal, setShowModal ] = useState(false);

	const { email: userEmail } = useCurrentUser();

	const email = userEmail ?? '';
	const isRequested = useSonarSelector(patchChangeEmailSelectors.isRequested);
	const isSuccessful = useSonarSelector(patchChangeEmailSelectors.isReceived);
	const isErrored = useSonarSelector(patchChangeEmailSelectors.isErrored);
	const isFailed = useSonarSelector(patchChangeEmailSelectors.isFailed);
	const requestStatusCode = useSonarSelector(patchChangeEmailSelectors.statusCode);

	const requestEmailUpdate = useAction(securityActions.requestPatchSecurityAuthenticationChangeEmail);
	const resetEmailUpdateRequest = useAction(securityActions.resetPatchSecurityAuthenticationChangeEmail);

	const handleOnEmailFinish = useCallback(() => {
		setShowModal(true);
	}, []);

	const handleOnCancelClick = useCallback(() => {
		setShowModal(false);
	}, []);

	const failureMessage =
		requestStatusCode === ErrorOrFailStatusCode.Conflict ?
			COPY_SPECIFIC.EMAIL_IN_USE :
			NOTIFICATIONS_COPY.FAILED_PATCH_USER_CHANGE_EMAIL;

	const handleOnOkClick = useCallback(
		(currentPassword: string) => {
			const { current } = emailFormRef;

			if (current) {
				const newEmail = current.getFieldValue('email') ?? '';

				requestEmailUpdate({
					currentPassword,
					email: newEmail.trim()
				});
			}
		},
		[ requestEmailUpdate ]
	);

	useEffect(() => {
		if (isSuccessful) {
			setShowModal(false);
			resetEmailUpdateRequest();
		}
	}, [ isSuccessful, resetEmailUpdateRequest ]);

	return (
		<div id='email'>
			<Form
				initialValues={ {
					currentPassword: null,
					email
				} }
				layout='vertical'
				name='email'
				onFinish={ handleOnEmailFinish }
				ref={ emailFormRef }
				requiredMark={ false }>
				<Email
					disabled={ isRequested }
					id='user-email'
					label={ COPY_SPECIFIC.EMAIL } />
				<Button
					disabled={ isRequested }
					htmlType='submit'
					id='user-email-submit-button'
					type='primary'>
					{ buttonText }
				</Button>
			</Form>

			<CurrentPasswordModal
				error={ isErrored }
				errorMessage={ NOTIFICATIONS_COPY.ERROR_PATCH_USER_CHANGE_EMAIL }
				failed={ isFailed }
				failedMessage={ failureMessage }
				onCancel={ handleOnCancelClick }
				onOk={ handleOnOkClick }
				open={ showModal } />

			<AsyncStateNotification
				received={ NOTIFICATIONS_COPY.SUCCESS_PATCH_USER_CHANGE_EMAIL }
				stateSelector={ patchChangeEmailSelectors.state } />
		</div>
	);
};
